import { TFunction } from "i18next";

import { ERRORS } from "../constants";
import { SimpleResult } from "../simple-result/simple-result";
import { TError } from "../simple-result/simple-result.models";
import { isLabelItem, isTranslatableItem } from "../simple-result/simple-result.utils";

/**
 * Turns any `TError` — or a whole `SimpleResult`, unwrapped first — into the
 * sentence a user reads, in whichever language is active. A `catch` block
 * often only has "whatever this rejected with" to hand a toast, and that can
 * itself be a `SimpleResult` a lower layer already built.
 */
export const normalizeError = (
  error: TError | SimpleResult<unknown> | undefined,
  t: TFunction,
): string => {
  if (error instanceof SimpleResult) return normalizeError(error.error, t);
  if (isTranslatableItem(error)) return t(error.translationKey, error.translationParams);
  if (isLabelItem(error)) return error.label;
  if (Array.isArray(error)) return error.join(", ");
  if (typeof error === "string") return error;

  return t(ERRORS.common.unknown);
};

/** `normalizeError` over a batch — e.g. several failures a screen collected in parallel. */
export const normalizeErrors = (
  errors: Array<TError | SimpleResult<unknown> | undefined>,
  t: TFunction,
): string[] =>
  errors
    .filter((error): error is TError | SimpleResult<unknown> => error !== undefined)
    .map(error => normalizeError(error, t));
